/**
 * Project Drive Export Service
 * 
 * Exports complete project bundles to Google Drive:
 * - Project manifest (.jenial)
 * - All generated videos
 * - All keyframe images
 * - Character images
 * - Folder structure matching project organization
 */

import { uploadToDrive, isDriveConnected, createDriveFolder } from './googleDriveClient';
import { Project, JenialProjectFile } from '../types/project';
import { getShotsByProject, getAssetsByProject, getAllCharacters } from './localMediaDB';
import { supabase } from './supabaseClient';

// ============================================================================
// TYPES
// ============================================================================

export interface DriveExportProgress {
    current: number;
    total: number;
    phase: 'preparing' | 'creating-folders' | 'uploading-videos' | 'uploading-images' | 'uploading-manifest' | 'complete';
    message: string;
}

export interface DriveExportResult {
    success: boolean;
    folderLink?: string;
    folderId?: string;
    filesUploaded: number;
    errors: string[];
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

const base64ToBlob = (base64: string, mimeType: string = 'image/png'): Blob => {
    const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;
    const byteCharacters = atob(base64Data);
    const byteArray = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteArray[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([byteArray], { type: mimeType });
};

// ============================================================================
// AUTO UPLOAD (Single video after generation)
// ============================================================================

export const autoUploadVideoToDrive = async (
    videoUrl: string,
    projectName: string,
    segmentIndex: number
): Promise<{ success: boolean; driveLink?: string; error?: string }> => {
    try {
        const connected = await isDriveConnected();
        if (!connected) {
            return { success: false, error: 'NOT_CONNECTED' };
        }

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        const filename = `${projectName}_segment${segmentIndex}_${timestamp}.mp4`;

        const result = await uploadToDrive(videoUrl, filename, 'video/mp4');

        if (result.success) {
            return { success: true, driveLink: result.webViewLink };
        }
        return { success: false, error: result.error };
    } catch (error) {
        return { success: false, error: 'UPLOAD_ERROR' };
    }
};

// ============================================================================
// COMPLETE PROJECT BUNDLE EXPORT
// ============================================================================

/**
 * Export complete project bundle to Google Drive
 * 
 * Creates folder structure:
 * /Jenial_ProjectName_timestamp/
 *   ├── project.jenial (manifest with all metadata)
 *   ├── videos/
 *   │   ├── shot_001.mp4
 *   │   └── shot_002.mp4
 *   ├── keyframes/
 *   │   ├── segment_0.png
 *   │   └── segment_1.png
 *   └── characters/
 *       ├── character1.png
 *       └── character2.png
 */
export const exportProjectBundleToDrive = async (
    project: Project,
    additionalData: {
        promptSequence?: any;
        dogma?: any;
        storyboardByIndex?: Record<number, { previewImage?: { base64?: string } }>;
        sequenceVideoData?: Record<number, { videoUrl?: string; thumbnailBase64?: string }>;
        timelineSegments?: any[];
    } = {},
    onProgress?: (progress: DriveExportProgress) => void
): Promise<DriveExportResult> => {
    const errors: string[] = [];
    let filesUploaded = 0;

    try {
        // Check connection
        const connected = await isDriveConnected();
        if (!connected) {
            return { success: false, filesUploaded: 0, errors: ['Google Drive not connected'] };
        }

        onProgress?.({ current: 0, total: 100, phase: 'preparing', message: 'Preparing export...' });

        // Get all project data
        const shots = await getShotsByProject(project.id);
        const assets = await getAssetsByProject(project.id);
        const characters = await getAllCharacters();

        // Calculate total items
        const videos = shots.filter(s => s.videoUrl);
        const keyframes = Object.values(additionalData.storyboardByIndex || {}).filter(k => k?.previewImage?.base64);
        const characterImages = characters.flatMap(c => c.images || []);

        const totalItems = 1 + videos.length + keyframes.length + characterImages.length; // +1 for manifest

        onProgress?.({ current: 0, total: totalItems, phase: 'creating-folders', message: 'Creating folder structure...' });

        // Create main project folder
        const timestamp = new Date().toISOString().slice(0, 10);
        const folderName = `Jenial_${project.name.replace(/[^a-z0-9]/gi, '_')}_${timestamp}`;
        const mainFolder = await createDriveFolder(folderName);

        if (!mainFolder) {
            return { success: false, filesUploaded: 0, errors: ['Failed to create project folder'] };
        }

        // Create subfolders
        const videosFolder = await createDriveFolder('videos', mainFolder.id);
        const keyframesFolder = await createDriveFolder('keyframes', mainFolder.id);
        const charactersFolder = await createDriveFolder('characters', mainFolder.id);

        let currentItem = 0;

        // Upload videos
        onProgress?.({ current: currentItem, total: totalItems, phase: 'uploading-videos', message: 'Uploading videos...' });

        for (let i = 0; i < videos.length; i++) {
            const shot = videos[i];
            if (shot.videoUrl) {
                const filename = `shot_${String(i + 1).padStart(3, '0')}_${shot.id.slice(0, 8)}.mp4`;
                const result = await uploadToDrive(shot.videoUrl, filename, 'video/mp4', videosFolder?.id);
                if (result.success) {
                    filesUploaded++;
                } else {
                    errors.push(`Failed to upload video: ${filename}`);
                }
            }
            currentItem++;
            onProgress?.({ current: currentItem, total: totalItems, phase: 'uploading-videos', message: `Uploading video ${i + 1}/${videos.length}...` });
        }

        // Upload keyframes
        onProgress?.({ current: currentItem, total: totalItems, phase: 'uploading-images', message: 'Uploading keyframes...' });

        const keyframeEntries = Object.entries(additionalData.storyboardByIndex || {});
        for (const [index, keyframe] of keyframeEntries) {
            if (keyframe?.previewImage?.base64) {
                const blob = base64ToBlob(keyframe.previewImage.base64);
                const tempUrl = URL.createObjectURL(blob);
                const filename = `keyframe_segment_${index}.png`;
                const result = await uploadToDrive(tempUrl, filename, 'image/png', keyframesFolder?.id);
                URL.revokeObjectURL(tempUrl);
                if (result.success) {
                    filesUploaded++;
                }
            }
            currentItem++;
        }

        // Upload character images
        onProgress?.({ current: currentItem, total: totalItems, phase: 'uploading-images', message: 'Uploading characters...' });

        for (const character of characters) {
            for (let i = 0; i < (character.images?.length || 0); i++) {
                const img = character.images[i];
                // LocalCharacterImage has blob, not base64
                if (img?.blob) {
                    const tempUrl = URL.createObjectURL(img.blob);
                    const filename = `${character.name.replace(/[^a-z0-9]/gi, '_')}_${i + 1}.png`;
                    const result = await uploadToDrive(tempUrl, filename, img.type || 'image/png', charactersFolder?.id);
                    URL.revokeObjectURL(tempUrl);
                    if (result.success) {
                        filesUploaded++;
                    }
                }
                currentItem++;
            }
        }

        // Create and upload manifest (.jenial)
        onProgress?.({ current: currentItem, total: totalItems, phase: 'uploading-manifest', message: 'Creating project manifest...' });

        const manifest: JenialProjectFile = {
            version: '1.0',
            project,
            shots: await Promise.all(shots.map(async (shot) => ({
                id: shot.id,
                prompt: shot.prompt,
                thumbnail: shot.thumbnailBlob ? await blobToBase64(shot.thumbnailBlob) : undefined,
                videoRef: shot.videoUrl || undefined,
            }))),
            characters: characters as any,
            segments: additionalData.timelineSegments?.map(seg => ({
                id: seg.id,
                trackId: seg.trackId,
                inSec: seg.inSec,
                outSec: seg.outSec,
                sourceInSec: seg.sourceInSec,
                label: seg.label,
                shotId: seg.mediaId,
            })),
            promptSequence: additionalData.promptSequence,
            dogma: additionalData.dogma,
        };

        const manifestJson = JSON.stringify(manifest, null, 2);
        const manifestBlob = new Blob([manifestJson], { type: 'application/json' });
        const manifestUrl = URL.createObjectURL(manifestBlob);
        const manifestResult = await uploadToDrive(manifestUrl, 'project.jenial', 'application/json', mainFolder.id);
        URL.revokeObjectURL(manifestUrl);

        if (manifestResult.success) {
            filesUploaded++;
        } else {
            errors.push('Failed to upload project manifest');
        }

        currentItem++;
        onProgress?.({ current: currentItem, total: totalItems, phase: 'complete', message: `Export complete! ${filesUploaded} files uploaded.` });

        return {
            success: errors.length === 0,
            folderId: mainFolder.id,
            folderLink: `https://drive.google.com/drive/folders/${mainFolder.id}`,
            filesUploaded,
            errors,
        };
    } catch (error) {
        console.error('[DriveExport] Bundle export error:', error);
        return {
            success: false,
            filesUploaded,
            errors: [...errors, String(error)],
        };
    }
};

// Legacy export function (for backwards compatibility)
export const exportProjectToDrive = exportProjectBundleToDrive;
